import { FC, useState } from 'react';
import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { PropertyFormData } from '@/types/index';
import { Input } from '@components/common/Input';
import { Slider } from '@components/common/Slider';
import { RadioGroup } from '@components/common/RadioGroup';
import clsx from 'clsx';

interface Step4StateProps {
  register: UseFormRegister<PropertyFormData>;
  errors: FieldErrors<PropertyFormData>;
  setValue: UseFormSetValue<PropertyFormData>;
  watch: UseFormWatch<PropertyFormData>;
}

const CONDITION_LABELS = ['Mauvais', 'Moyen', 'Bon', 'Excellent'];
const CONDITION_VALUES = ['poor', 'average', 'good', 'excellent'];

const conditionToIndex = (value?: string): number => {
  const idx = CONDITION_VALUES.indexOf(value || '');
  return idx >= 0 ? idx : 1;
};

const indexToCondition = (index: number): string => {
  return CONDITION_VALUES[index] || 'average';
};

interface CollapsibleGroupProps {
  title: string;
  defaultOpen: boolean;
  children: React.ReactNode;
}

const CollapsibleGroup: FC<CollapsibleGroupProps> = ({ title, defaultOpen, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'w-full flex items-center justify-between px-4 py-3',
          'bg-gray-50 dark:bg-gray-700/50',
          'hover:bg-gray-100 dark:hover:bg-gray-700',
          'transition-colors cursor-pointer'
        )}
      >
        <span className="text-base font-semibold text-gray-800 dark:text-gray-200">
          {title}
        </span>
        <svg
          className={clsx(
            'w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        className={clsx(
          'transition-all duration-200 ease-in-out overflow-hidden',
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="p-4 space-y-4">
          {children}
        </div>
      </div>
    </div>
  );
};

export const Step4State: FC<Step4StateProps> = ({
  register,
  errors,
  setValue,
  watch,
}) => {
  const currentYear = new Date().getFullYear();

  // Watch form values for sliders
  const buildingGeneralAspect = watch('buildingGeneralAspect');
  const buildingStructure = watch('buildingStructure');
  const buildingRoof = watch('buildingRoof');
  const buildingFacades = watch('buildingFacades');
  const buildingWindows = watch('buildingWindows');
  const buildingCorridors = watch('buildingCorridors');
  const buildingTechnicalInstall = watch('buildingTechnicalInstall');
  const hasCEBC = watch('hasCEBC');
  const hasMinergie = watch('hasMinergie');

  const condPlastering = watch('condPlastering');
  const condParquet = watch('condParquet');
  const condTiling = watch('condTiling');
  const condElectricity = watch('condElectricity');
  const condPlumbing = watch('condPlumbing');
  const condJoinery = watch('condJoinery');
  const condKitchen = watch('condKitchen');

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        État
      </h2>

      {/* Année de construction */}
      <div className="space-y-4">
        <Input
          label="Année de construction"
          type="number"
          {...register('buildingYear', {
            valueAsNumber: true,
            min: { value: 1800, message: 'L\'année doit être au moins 1800' },
            max: { value: currentYear, message: `L'année ne peut pas dépasser ${currentYear}` },
          })}
          error={errors.buildingYear?.message}
          placeholder="1970"
        />
      </div>

      {/* Groupe 1 : État de l'immeuble (fermé par défaut) */}
      <CollapsibleGroup title="État de l'immeuble" defaultOpen={false}>
        <Slider
          label="Aspect général"
          value={conditionToIndex(buildingGeneralAspect)}
          onChange={(v) => setValue('buildingGeneralAspect', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Structure"
          value={conditionToIndex(buildingStructure)}
          onChange={(v) => setValue('buildingStructure', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Toit"
          value={conditionToIndex(buildingRoof)}
          onChange={(v) => setValue('buildingRoof', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Façades"
          value={conditionToIndex(buildingFacades)}
          onChange={(v) => setValue('buildingFacades', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Fenêtres"
          value={conditionToIndex(buildingWindows)}
          onChange={(v) => setValue('buildingWindows', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Corridors"
          value={conditionToIndex(buildingCorridors)}
          onChange={(v) => setValue('buildingCorridors', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Installations techniques"
          value={conditionToIndex(buildingTechnicalInstall)}
          onChange={(v) => setValue('buildingTechnicalInstall', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <RadioGroup
            label="CEBC"
            name="hasCEBC"
            options={[
              { value: 'true', label: 'Oui' },
              { value: 'false', label: 'Non' },
            ]}
            value={hasCEBC ? 'true' : 'false'}
            onChange={(v) => setValue('hasCEBC', v === 'true')}
          />
          <RadioGroup
            label="Minergie"
            name="hasMinergie"
            options={[
              { value: 'true', label: 'Oui' },
              { value: 'false', label: 'Non' },
            ]}
            value={hasMinergie ? 'true' : 'false'}
            onChange={(v) => setValue('hasMinergie', v === 'true')}
          />
        </div>
      </CollapsibleGroup>

      {/* Groupe 2 : État de l'appartement (ouvert par défaut) */}
      <CollapsibleGroup title="État de l'appartement" defaultOpen={true}>
        <Slider
          label="Plâtrerie-peinture"
          value={conditionToIndex(condPlastering)}
          onChange={(v) => setValue('condPlastering', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Parquet"
          value={conditionToIndex(condParquet)}
          onChange={(v) => setValue('condParquet', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Carrelage"
          value={conditionToIndex(condTiling)}
          onChange={(v) => setValue('condTiling', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Électricité"
          value={conditionToIndex(condElectricity)}
          onChange={(v) => setValue('condElectricity', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Sanitaire-chauffage"
          value={conditionToIndex(condPlumbing)}
          onChange={(v) => setValue('condPlumbing', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Menuiserie"
          value={conditionToIndex(condJoinery)}
          onChange={(v) => setValue('condJoinery', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
        <Slider
          label="Cuisine"
          value={conditionToIndex(condKitchen)}
          onChange={(v) => setValue('condKitchen', indexToCondition(v))}
          min={0}
          max={3}
          labels={CONDITION_LABELS}
        />
      </CollapsibleGroup>
    </div>
  );
};
